<template>
  <div>
    <vx-card class="w-full">
      <div class="flex justify-between items-center">
        <h4>수도 엑셀(FMS) 업로드</h4>
        <vs-button color="secondary" @click="onClickSave">등록</vs-button>
      </div>

      <!-- 기본정보 -->
      <FieldSelectModule
        title="작성자 정보"
        v-model="form.stdInfo"
        :typeslabel="{
          date: '등록일',
        }"
        :board="board"
        :types="['department', 'com_name', 'rank', 'writer_name', 'date']"
        class="mt-28px"
      />

      <!-- 파일 업로드 -->
      <div class="mt-28px">
        <div class="writeGridContainer">
          <div class="writeGridLineItem">
            <div>파일 업로드</div>
            <div>
              <vs-button color="secondary" type="border" :href="excelLink">엑셀 템플릿 다운로드</vs-button>
              <vs-button color="secondary" class="ml-2" @click="onClickExcelUpload"> 작성 엑셀 업로드 </vs-button>
              <input v-show="false" ref="excelInput" type="file" multiple @change="readFile" accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-between items-center mt-6 mb-4">
        <h5><i class="dot"></i> 등록 대상 시설물 정보</h5>
      </div>

      <div>
        <table class="excel-table">
          <colgroup>
            <col width="50" />
            <col width="60" />
            <col width="400" />
            <col v-for="_ in 7" :key="`col_${_}`" />
            <col width="120" />
          </colgroup>
          <thead>
          <tr>
            <th></th>
            <th>No.</th>
            <th>관리 부서 및 업체 지정</th>
            <th>시설물번호</th>
            <th>관리번호</th>
            <th>시설물명</th>
            <th>관리주체</th>
            <th>소유자</th>
            <th>전화번호</th>
            <th>보관자</th>
            <th>수정/삭제</th>
          </tr>
          </thead>
          <tbody v-for="(excel, excel_i) in excelData" :key="`${excel_i}_${excel[0]}`">
          <tr>
            <td>
              <img src="@/assets/icons/triangle_down.svg" class="mx-auto duration-200 cursor-pointer" :style="{ transform: excel.open ? 'rotate(0deg)' : 'rotate(-90deg)' }" @click="excel.open = !excel.open" />
            </td>
            <td>{{ excel_i + 1 }}</td>
            <td v-for="(col, col_key) in excel.row" :key="`${excel_i}_td_${col_key}`">
              <div class="flex items-center justify-center gap-2">
                <CompanySelector v-if="col_key === '관리부서및업체지정'" multiple class="w-full px-4" v-model="excel.row[col_key]" :show-empty-message="true" />
                <p v-else-if="col_key === '시설물번호'">{{ excel.form.facilityNumber || "-" }}</p>
                <p v-else-if="col_key === '관리번호'">{{ excel.form.managementNumber || "-" }}</p>
                <p v-else-if="col_key === '시설물명'">{{ excel.form.facilityName || "-" }}</p>
                <p v-else-if="col_key === '관리주체'">{{ excel.form.management_entity || "-" }}</p>
                <p v-else-if="col_key === '소유자'">{{ excel.form.owner || "-" }}</p>
                <p v-else-if="col_key === '전화번호'">{{ excel.form.phoneNumber || "-" }}</p>
                <p v-else-if="col_key === '보관자'">{{ excel.form.custodian || "-" }}</p>
                <p v-else>{{ col || "-" }}</p>
              </div>
              <p v-if="excel.err && excel.err[col_key]" class="errMsg">{{ excel.err[_num_i] }}</p>
            </td>
            <td>
              <div class="flex justify-center gap-3">
                <fragment v-if="editExcelIndex === excel_i">
                  <button @click="onClickFinishExcelData(excel_i)">
                    <feather-icon icon="CheckIcon" svgClasses="h-5 w-5" />
                  </button>
                  <button @click="onClickCancelExcelData(excel_i)">
                    <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
                  </button>
                </fragment>
                <fragment v-else>
                  <button @click="onClickEditExcelData(excel_i)">
                    <feather-icon icon="Edit2Icon" svgClasses="h-5 w-5" />
                  </button>
                  <button @click="onClickRemoveExcelData(excel_i)">
                    <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" />
                  </button>
                </fragment>
              </div>
            </td>
          </tr>
          <tr v-show="excel.open">
            <td colspan="11" class="p-0">
              <waterworks-write-body
                :isPrint="!editExcelData"
                :form="editExcelData ? editExcelData.form : excel.form"
                :init-cat1-list="cat1List"
                :category-info="excel.categoryInfo"
              />
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </vx-card>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FieldFacilityBridgeWrite from "@/views/_BS/FieldFacilityBridgeWrite.vue";
import CompanySelector from "@/components/selector/CompanySelector.vue";

import XLSX from "xlsx";
import moment from "moment";
import config from "/public/config";
import {levenshteinDistance} from "@/util/DataUtil";
import SewageTreatmentWriteBody from "@/views/fields/single-page/sewage-treatment/SewageTreatmentWriteBody.vue";
import WaterworksWriteBody from "@/views/fields/single-page/waterworks/WaterworksWriteBody.vue";

export default {
  name: "PortExcelUpload",
  components: {
    WaterworksWriteBody,
    SewageTreatmentWriteBody,
    FieldSelectModule,
    FieldFacilityBridgeWrite,
    CompanySelector,
  },
  data() {
    return {
      board: {
        projectTitle: this.$store.state.project.projectInfo.field_name,
        writer: {}, //작성자 정보
        readonly: false, // 읽기전용인가
        isPrint: false,
        viewSaveBtn: true,
        viewRemoveBtn: true,
      },
      form: {
        stdInfo: {},
      },

      excelData: [],

      editExcelIndex: null,
      editExcelData: null,

      cat1List: [],
      facilityCategoryList: [],
    };
  },
  computed: {
    projectId() {
      return Number(this.$route.params.proj_id);
    },
    boardId() {
      return Number(this.$route.name.slice(-3));
    },
    myInfo() {
      return this.$store.state.member.memberInfo;
    },
    excelLink() {
      return `${config.BASE_API_URL}/storage/app/media/%EC%8B%9C%EC%84%A4%EB%AC%BC%EA%B4%80%EB%A6%AC%EB%8C%80%EC%9E%A5_%EC%88%98%EB%8F%84.xls`;
    },
  },
  async mounted() {
    await this.init();
  },
  methods: {
    async init() {
      await this.$store.dispatch("USER_INFO");
      this.board.writer = {
        id: this.$store.state.auth.userInfo.id,
        name: this.$store.state.auth.userInfo.name,
        company_id: this.$store.state.auth.userInfo.company_id,
        department: this.$store.state.auth.userInfo.department,
        section: this.$store.state.auth.userInfo.section,
        rank: this.$store.state.auth.userInfo.rank,
        com_name: "",
      };

      await this.$store.dispatch("company/LOAD_COMPANY", {
        companyId: this.board.writer.company_id,
      });
      this.board.writer.com_name = this.$store.state.company.companyInfo.com_name;
      if (!this.board.writer.section) {
        this.board.writer.section = this.board.writer.com_name;
      }

      // 시설물 구분 값을 불러온다
      const response = await this.$store.dispatch("project/LOAD_FIELD_FACILITY_CATEGORY_LIST", {});
      if (response.data) {
        this.facilityCategoryList = response.data;
        const _cat1List = this.facilityCategoryList.map((item) => item.cat1);
        this.cat1List = this.setCatOther(_cat1List);
      }
    },

    /**
     * 주어진 cat1과 cat3에 가장 가까운 시설 카테고리를 찾는 함수입니다.
     * 이 함수는 레벤슈타인 거리(Levenshtein Distance)를 사용하여 cat3와 각 시설 카테고리 간의 '편집 거리'를 계산하고,
     * 가장 작은 편집 거리를 가진 시설 카테고리를 반환합니다.
     *
     * @param {string} cat1 - 시설 카테고리의 첫 번째 분류
     * @param {string} cat3 - 시설 카테고리의 세 번째 분류
     * @returns {Object} cat3와 가장 가까운 시설 카테고리
     */
    findFacilityCategory(cat1, cat3) {
      const cat1Items = this.facilityCategoryList.filter((item) => item.cat1 === cat1);
      if (cat1Items.length === 0) {
        return [];
      }
      return cat1Items.reduce((prev, curr) => {
        const prevDistance = levenshteinDistance(cat3, prev.cat3);
        const currDistance = levenshteinDistance(cat3, curr.cat3);
        return (prevDistance < currDistance) ? prev : curr;
      });
    },

    onClickExcelUpload() {
      const excelInput = this.$refs.excelInput;
      excelInput.value = null;
      excelInput.click();
    },
    // 엑셀데이터 파싱
    async readFile(event) {
      // get File object from input tag
      for (let i = 0; i < event.target.files.length; i++) {
        const file = event.target.files[i];
        // declare FileReader, temp result
        const reader = new FileReader();
        const tmpResult = {};

        // if you use "this", don't use "function(e) {...}"
        reader.onload = async (e) => {
          let data = e.target.result;
          data = new Uint8Array(data);
          // get excel file
          const excelFile = XLSX.read(data, { type: "array", cellDates: true });
          const sheetData = excelFile.Sheets[excelFile.SheetNames[0]];

          let 관리주체구분 = "";
          if (this.getCellData(sheetData, "AK23")) {
            관리주체구분 = "민간";
          } else if (this.getCellData(sheetData, "X25")) {
            관리주체구분 = "중앙부처";
          } else if (this.getCellData(sheetData, "AB25")) {
            관리주체구분 = "지자체";
          } else if (this.getCellData(sheetData, "AG25")) {
            관리주체구분 = "(국가/지방)공기업";
          }

          let 소유자구분 = "";
          if (this.getCellData(sheetData, "BF23")) {
            소유자구분 = "민간";
          } else if (this.getCellData(sheetData, "AU25")) {
            소유자구분 = "중앙부처";
          } else if (this.getCellData(sheetData, "BA25")) {
            소유자구분 = "지자체";
          } else if (this.getCellData(sheetData, "BC25")) {
            소유자구분 = "(국가/지방)공기업";
          }

          const addresses = [];
          if (this.getCellData(sheetData, "B22")) addresses.push(this.getCellData(sheetData, "B22"));
          if (this.getCellData(sheetData, "C22")) addresses.push(this.getCellData(sheetData, "C22"));
          if (this.getCellData(sheetData, "G22")) addresses.push(this.getCellData(sheetData, "G22"));
          if (this.getCellData(sheetData, "M22")) addresses.push(this.getCellData(sheetData, "M22"));
          if (this.getCellData(sheetData, "M24")) addresses.push(this.getCellData(sheetData, "M24"));

          const 전화번호 = this.getCellData(sheetData, "AJ14").replace(/ /g, "").replace("전화번호:", "").trim();
          const 시설물번호 = this.getCellData(sheetData, "V4")
          const 관리번호 = this.getCellData(sheetData, "V5")
          const 시설물명 = this.getCellData(sheetData, "V6")
          const 관리주체 = this.getCellData(sheetData, "T13")
          const 소유자 = this.getCellData(sheetData, "T14")
          const 보관자 = this.getCellData(sheetData, "T15")

          const cat1 = this.getCellData(sheetData, "AX20");
          const cat3 = this.getCellData(sheetData, "AK20");
          const facilityCategory = this.findFacilityCategory(cat1, cat3);

          const 상세제원 = {
            취수시설: this.getCellData(sheetData, "P41").replace('개', '').trim(),
            취수장: this.getCellData(sheetData, "S41").replace('개', '').trim(),
            정수장: this.getCellData(sheetData, "W41").replace('개', '').trim(),
            가압장: this.getCellData(sheetData, "AC41").replace('개', '').trim(),
            배수지: this.getCellData(sheetData, "AI41").replace('개', '').trim(),
            수로터널: this.getCellData(sheetData, "AO41").replace('개', '').trim(),
            취수댐: this.getCellData(sheetData, "AS41").replace('개', '').trim(),
            수관교: this.getCellData(sheetData, "AY41").replace('개', '').trim(),
            관로시설: this.getCellData(sheetData, "BE41").replace('㎞', '').trim(),
          }

          const 취수시설 = [];
          const 취수장 = [];
          const 정수장 = [];
          const 가압장 = [];
          const 배수지 = [];
          const 각종밸브실 = {
            총개소: "0",
            제수밸브실: "0",
            공기밸브실: "0",
            이토밸브실: "0",
            신축관실: "0",
            기타: "0",
          };
          const 관로시설 = [];

          // 취수시설
          let row = 45
          if (상세제원.취수시설 === 0) row += 3;
          for (let i = 0; i < 상세제원.취수시설; i++) {
            if (i > 0) {
              row += 3;
            }
            취수시설.push({
              명칭: this.getCellData(sheetData, `B${row}`),
              시설용량: this.getCellData(sheetData, `J${row}`).replace('m³/일', '').trim(),
              취수원: this.getCellData(sheetData, `Q${row}`),
              취수형식: this.getCellData(sheetData, `U${row}`),
              하천수계: this.getCellData(sheetData, `AA${row}`),
              하천명: this.getCellData(sheetData, `AI${row}`),
              하천등급: this.getCellData(sheetData, `AP${row}`),
              부대시설: this.getCellData(sheetData, `AW${row}`),
              주소: this.getCellData(sheetData, `Q${row + 1}`),
              관련정수장: this.getCellData(sheetData, `AP${row + 1}`),
            });
          }

          // 취수장
          row += 5
          if (상세제원.취수장 === 0) row += 3;
          for (let i = 0; i < 상세제원.취수장; i++) {
            if (i > 0) {
              row += 3;
            }
            취수장.push({
              명칭: this.getCellData(sheetData, `B${row}`),
              시설용량: this.getCellData(sheetData, `J${row}`).replace('m³/일', '').trim(),
              펌프사양: this.getCellData(sheetData, `Q${row}`),
              건축물연면적: this.getCellData(sheetData, `AI${row}`).replace('m²', '').trim(),
              부대시설: this.getCellData(sheetData, `AP${row}`),
              주소: this.getCellData(sheetData, `Q${row + 1}`),
              관련정수장: this.getCellData(sheetData, `AP${row + 1}`),
            });
          }

          // 정수장
          row += 6
          if (상세제원.정수장 === 0) row += 7;
          for (let i = 0; i < 상세제원.정수장; i++) {
            if (i > 0) {
              row += 7;
            }
            정수장.push({
              명칭: this.getCellData(sheetData, `B${row}`),
              시설용량: this.getCellData(sheetData, `J${row}`).replace('m³/일', '').trim(),
              착수정_형식: this.getCellData(sheetData, `R${row}`),
              착수정_개수: this.getCellData(sheetData, `W${row}`),
              혼화지_형식: this.getCellData(sheetData, `AE${row}`),
              혼화지_개수: this.getCellData(sheetData, `AN${row}`),
              응접침전지_형식: this.getCellData(sheetData, `AT${row}`),
              응접침전지_개수: this.getCellData(sheetData, `BE${row}`),
              여과지_형식: this.getCellData(sheetData, `J${row + 3}`),
              여과지_개수: this.getCellData(sheetData, `R${row + 3}`),
              정수지_형식: this.getCellData(sheetData, `W${row + 3}`),
              정수지_개수: this.getCellData(sheetData, `AE${row + 3}`),
              고도처리시설: this.getCellData(sheetData, `AN${row + 3}`),
              배출수처리시설: this.getCellData(sheetData, `AT${row + 3}`),
              건축물연면적: this.getCellData(sheetData, `BE${row + 3}`).replace('m²', '').trim(),
              주소: this.getCellData(sheetData, `R${row + 4}`),
              관련정수장: this.getCellData(sheetData, `AT${row + 4}`),
            });
          }

          // 가압장
          row += 8
          if (상세제원.가압장 === 0) row += 3;
          for (let i = 0; i < 상세제원.가압장; i++) {
            if (i > 0) {
              row += 3;
            }
            가압장.push({
              명칭: this.getCellData(sheetData, `B${row}`),
              시설용량: this.getCellData(sheetData, `J${row}`).replace('m³/일', '').trim(),
              펌프사양: this.getCellData(sheetData, `Q${row}`),
              건축물연면적: this.getCellData(sheetData, `AI${row}`).replace('m²', '').trim(),
              부대시설: this.getCellData(sheetData, `AP${row}`),
              주소: this.getCellData(sheetData, `Q${row + 1}`),
              관련정수장: this.getCellData(sheetData, `AP${row + 1}`),
            });
          }

          // 조절지 및 배수지
          row += 5
          if (상세제원.배수지 === 0) row += 3;
          for (let i = 0; i < 상세제원.배수지; i++) {
            if (i > 0) {
              row += 3;
            }
            배수지.push({
              명칭: this.getCellData(sheetData, `B${row}`),
              시설용적: this.getCellData(sheetData, `J${row}`).replace('m³', '').trim(),
              개수: this.getCellData(sheetData, `Q${row}`),
              건축물연면적: this.getCellData(sheetData, `AI${row}`).replace('m²', '').trim(),
              부대시설: this.getCellData(sheetData, `AP${row}`),
              주소: this.getCellData(sheetData, `Q${row + 1}`),
              관련정수장: this.getCellData(sheetData, `AP${row + 1}`),
            });
          }

          // 각종 밸브실
          row += 5
          각종밸브실.총개소 = this.getCellData(sheetData, `B${row}`).replace('개소', '').trim();
          각종밸브실.제수밸브실 = this.getCellData(sheetData, `J${row}`).replace('개소', '').trim();
          각종밸브실.공기밸브실 = this.getCellData(sheetData, `S${row}`).replace('개소', '').trim();
          각종밸브실.이토밸브실 = this.getCellData(sheetData, `AB${row}`).replace('개소', '').trim();
          각종밸브실.신축관실 = this.getCellData(sheetData, `AL${row}`).replace('개소', '').trim();
          각종밸브실.기타 = this.getCellData(sheetData, `AW${row}`).replace('개소', '').trim();

          // 관로시설
          // 관로시설의 갯수를 확인하기 위해 루프를 돌면서 "계통명칭" 인 셀을 찾는다
          // "계통명칭" 이라는 셀이 없을 경우, 관로시설이 없는 것으로 간주한다 (row -= 3)
          row += 4;
          while (this.getCellData(sheetData, `B${row - 1}`) === '계통명칭') {
            const 계통명칭 = this.getCellData(sheetData, `B${row}`)
            const 관로구분 = this.getCellData(sheetData, `J${row}`)
            const 관종 = this.getCellData(sheetData, `S${row}`)
            const 관경 = this.getCellData(sheetData, `Y${row}`)
            const 구간 = this.getCellData(sheetData, `AF${row}`)
            const 연장 = this.getCellData(sheetData, `AL${row}`)
            const 방식설비종류 = this.getCellData(sheetData, `AV${row}`)
            const 주소 = this.getCellData(sheetData, `BE${row}`)
            const 관련정수장 = this.getCellData(sheetData, `BN${row}`)

            if (계통명칭 || 관로구분 || 관종 || 관경 || 구간 || 연장 || 방식설비종류 || 주소 || 관련정수장) {
              관로시설.push({ id: 관로시설.length + 1,
                계통명칭, 관로구분, 관종, 관경, 구간, 연장, 방식설비종류, 주소, 관련정수장
              })
            }
            row += 3;
          }
          row -= 3;

          this.excelData.push({
            open: false,
            err: [],
            row: {
              관리부서및업체지정: "",
              시설물번호,
              관리번호,
              시설물명,
              관리주체,
              소유자,
              전화번호,
              보관자
            },
            form: {
              phoneNumber: 전화번호,
              custodian: 보관자, //  보관자

              facilityNumber: 시설물번호, //시설물번호
              managementNumber: 관리번호, //관리번호
              facilityName: 시설물명, //시설물명
              routeDivision: this.getCellData(sheetData, "Y20"), //노선-구분
              routeDetail: this.getCellData(sheetData, "AD20"), //노선-세부노선

              facility_category_id: facilityCategory.id,
              // 시설물 구분
              cat1: facilityCategory.cat1,
              // 시설물 종류
              cat2: facilityCategory.cat2,
              cat3: facilityCategory.cat3,
              // 시설물 종별
              classification: facilityCategory.classification,

              socEvaluationTarget: this.getCellData(sheetData, "BD20"), //SOC성능평가대상
              address: addresses.join(" "), //주소
              management_entity: 관리주체, //관리주체
              management_type: 관리주체구분, //관리주체구분
              owner: 소유자, //소유자
              ownerType: 소유자구분, //소유자구분
              businessPlanApprovalDate: this.setMoment(this.getCellData(sheetData, "B27")), //사업계획승인일
              completionDate: this.setMoment(this.getCellData(sheetData, "F27")), // 준공일
              defectLiabilityExpirationDate1: this.setMoment(this.getCellData(sheetData, "N27")), // 하자보수책임만료일
              detailedSpecifications: this.getCellData(sheetData, "S27"), // 상세제원
              safetyInspectionAndDiagnosisHistory: this.getCellData(sheetData, "X27"), // 안전점검진단이력
              repairAndReinforcementHistory: this.getCellData(sheetData, "AF27"), // 보수보강이력
              accessibleVentCover: this.getCellData(sheetData, "AM27"), // 접근가능한통풍구
              designPeriod: this.convertPeriodDate(this.getCellData(sheetData, "B29")), // 설계기간
              designer: this.getCellData(sheetData, "Q29"), // 설계자
              constructionPeriod: this.convertPeriodDate(this.getCellData(sheetData, "Z29")), // 시공기간
              contractor: this.getCellData(sheetData, "AL29"), // 시공자
              totalConstructionCost: this.getCellData(sheetData, "AZ29"), // 총공사비
              seismicDesignTarget: this.getCellData(sheetData, "E31"), // 내진설계 대상 유무
              seismicDesignApplication: this.getCellData(sheetData, "Q31"), // 내진설계 적용 유무
              seismicDesignStandard: this.getCellData(sheetData, "Z31"), // 적용내진설계기준
              seismicPerformanceEvaluation: this.getCellData(sheetData, "AL31"), // 내진성능평가 실시 유무
              seismicReinforcement: this.getCellData(sheetData, "AZ31"), // 내진보강 유무
              young21Target: this.getCellData(sheetData, "B33"), // 청년21대상
              auditPeriod: this.convertPeriodDate(this.getCellData(sheetData, "E33")), // 감리기간
              auditor: this.getCellData(sheetData, "Q33"), // 감리자
              projectOwner: this.getCellData(sheetData, "Z33"), // 사업주체(발주자)
              constructionName: this.getCellData(sheetData, "AL33"), // 공사명
              constructionSupervisor: this.getCellData(sheetData, "AZ33"), // 공사감독·공사관리관
              creationDate: this.setMoment(this.getCellData(sheetData, "B36")), // 작성일
              author: this.$store.state.auth.userInfo ? this.$store.state.auth.userInfo.name : this.getCellData(sheetData, "L36").replace('(인)', '').trim(), // 작성자
              signature: "", // 서명
              lastModifiedDate: this.setMoment(this.getCellData(sheetData, "AA36")), // 최종수정일
              lastModifiedBy: this.getCellData(sheetData, 'AR36'), // 최종수정자
              remarks: this.getCellData(sheetData, "D37"), // 비고

              // 상세제원
              상세제원,
              취수시설,
              취수장,
              정수장,
              가압장,
              배수지,
              각종밸브실,
              관로시설,

              otherSpecifications: this.getCellData(sheetData, "P56"),
              companyList: [],
              photoList: [],
            },
            categoryInfo: {
              facility_category_id: facilityCategory.id,
              cat1: facilityCategory.cat1,
              cat2: facilityCategory.cat2,
              cat3: facilityCategory.cat3,
              classification: facilityCategory.classification,
            },
          });
        };

        reader.readAsArrayBuffer(file);
      }
    },


    getCellData(sheet, cell, key = "v") {
      // sheet[row]가 존재하고, 해당 cell 존재하는지 확인
      // sheet[cell].v : Raw value
      // sheet[cell].w : Formatted value
      // sheet[cell].t : Type
      if (sheet[cell] !== undefined) {
        return `${sheet[cell][key]}`.trim();
      } else {
        return "";
      }
    },

    inputDetail(data) {
      this.editExcelData.form = data;
    },
    onClickEditExcelData(index) {
      this.editExcelIndex = index;
      this.editExcelData = JSON.parse(JSON.stringify(this.excelData[index]));
    },
    onClickRemoveExcelData(index) {
      this.excelData.splice(index, 1);
    },
    onClickFinishExcelData(index) {
      this.$set(this.excelData, index, this.editExcelData);
      this.editExcelIndex = null;
      this.editExcelData = null;
    },
    onClickCancelExcelData(index) {
      this.editExcelIndex = null;
      this.editExcelData = null;
    },

    /**
     * 1978-01-01 ~ 1981-12-31 형태의 문자열을 받아서 sDate, eDate로 나눠서 리턴한다
     *
     * @param dateStr
     * @returns {{sDate: string, eDate: string}}
     */
    convertPeriodDate(dateStr) {
      function isDate(checkDate) {
        const date = new Date(checkDate);
        if (date.toString() === 'Invalid Date') {
          return false
        }
        return true;
      }
      const result = { sDate: "", eDate: "" };
      if (dateStr) {
        // designPeriodStr 값을 '~' 기호로 분리한다
        const periodArr = dateStr.split("~");
        // 만약 2개가 나온다면, 각각 트림하여 designPeriod에 sDate, eDate로 넣어준다
        if (periodArr.length === 2) {
          result.sDate = isDate(periodArr[0].trim()) ? periodArr[0].trim() : '';
          result.eDate = isDate(periodArr[1].trim()) ? periodArr[1].trim() : '';
        }
      }
      return result;
    },
    onClickSave() {
      // 엑셀 데이터가 없을때 에러
      if (this.excelData.length < 1) {
        this.$emit("customAlert", "수도 엑셀(FMS) 정보가 없습니다");
        return;
      }

      for (const post of this.excelData) {
        // 필수 : facilityNumber, facilityName,
        if (!post.form.facilityNumber) {
          this.$emit("customAlert", "시설물 번호를 입력해주세요.");
          return;
        }
        else if (!post.form.facilityName) {
          this.$emit("customAlert", "시설물명을 입력해주세요.");
          return;
        }
      }

      const posts = this.excelData.map((ed) => {
        const option = {
          project_id: this.projectId, // 프로젝트 ID
          field_name: ed.form.facilityName, // 현장(시설물, 사업장) 이름
          contract_code: ed.form.facilityNumber, // 계약번호
          address: ed.form.address, // 주소
          phone: ed.form.phoneNumber, // 대표번호
          be_completed_at: ed.form.completionDate, // 준공일
          deposit_amount: ed.form.totalConstructionCost, // 총공사비
        };

        // Category
        if (ed.form.facility_category_id) {
          option.facility_category_id = ed.form.facility_category_id;
        }

        // Company
        if (ed.row.관리부서및업체지정.length > 0) {
          option.company_ids = ed.row.관리부서및업체지정.map((com) => com.id);
        }

        // Meta
        option.meta = { ...ed.form };

        return option;
      });

      this.$emit("save", posts);
    },
    // 정렬 후 기타, 기타 외를 배열의 마지막으로 이동하는 함수
    setCatOther(list) {
      list.sort();

      const etcIndex = list.indexOf("기타");
      if (etcIndex > -1) {
        list.splice(etcIndex, 1);
        list.push("기타");
      }
      const etcOutIndex = list.indexOf("기타 외");
      if (etcOutIndex > -1) {
        list.splice(etcOutIndex, 1);
        list.push("기타 외");
      }

      return list;
    },
    setMoment(date) {
      if (!date) return "";
      else if (moment(date).format("YYYY-MM-DD") === "Invalid date") return "";
      else return moment(date).format("YYYY-MM-DD");
    },
  },
};
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  thead {
    th {
      text-align: center;
    }
  }

  th,
  td {
    padding: 0;
  }

  th {
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;

    background: #fafafa;
    border-top: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;

    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  .sub-table {
    th {
      background: #7e72f20d;
      border-top: 0;
      padding: 16px 10px;
    }
  }

  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }

  .check {
    justify-content: center !important;
    ::v-deep {
      span {
        margin: 0;
      }
    }
  }
}

.errMsg {
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  color: #ea5455;
  margin-top: 2px;
}
</style>
